/**
 * Workbench return: what the page re-seeds from the persisted stores when the
 * officer comes back to a bare /workbench. The canvas elements rehydrate on
 * their own; the page session chain and address (which drive the toolbar) and
 * the selected node did not, so the canvas showed with dead buttons.
 */

export interface ReturnSessionInput {
    elements: any[];
    sessionChain: string | null;
    sessionAddress: string | null;
    selectedNodeId: string | null;
}

export interface ReturnSession {
    chain: string | null;
    address: string | null;
    /** `data` of the node to select again, or null when it is no longer on the canvas. */
    selectedNodeData: any | null;
}

/** First non-edge element, the same fallback every draft restore uses for the root. */
function firstNode(elements: any[]): any | null {
    return elements.find((el) => el?.data && !el.data.source) ?? null;
}

/**
 * Decide the session after a plain return. Returns null when there is nothing
 * to restore (empty canvas). Prefers the persisted session store, falls back to
 * the first canvas node; the selected node must still exist on the canvas.
 */
export function resolveReturnSession(input: ReturnSessionInput): ReturnSession | null {
    const { elements } = input;
    if (!Array.isArray(elements) || elements.length === 0) return null;
    const first = firstNode(elements);
    const chain = input.sessionChain || first?.data?.chain || null;
    const address = input.sessionAddress || first?.data?.id || null;
    if (!chain && !address) return null;
    const selected = input.selectedNodeId
        ? elements.find((el) => el?.data?.id === input.selectedNodeId && !el.data.source)
        : null;
    return { chain, address, selectedNodeData: selected?.data ?? null };
}
